<template>
  <div class="container p-4">
    <h3 class="text-xl font-bold mb-3">Unidades (ejes temáticos)</h3>

    <CardPrime class="mb-4">
      <template #title>Filtros (obligatorios)</template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label class="block text-sm mb-1">Curso</label>
            <DropdownPrime
              v-model="selectedCursoId"
              :options="cursos"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Selecciona curso"
              class="w-full"
              showClear
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Asignatura</label>
            <DropdownPrime
              v-model="selectedAsignaturaId"
              :options="asignaturas"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Selecciona asignatura"
              class="w-full"
              showClear
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Filtrar glosa (opcional)</label>
            <InputText v-model="filtroGlosa" placeholder="Contiene..." class="w-full" />
          </div>
          <div class="flex gap-2">
            <ButtonPrime label="Aplicar" icon="pi pi-filter" @click="aplicarFiltros" />
            <ButtonPrime
              label="Nueva unidad"
              icon="pi pi-plus"
              class="p-button-success"
              :disabled="!puedeCargar"
              @click="abrirNuevo"
            />
          </div>
        </div>
        <p class="text-sm text-gray-600 mt-2">
          Se listan las unidades que aparecen en los capítulos de este curso y asignatura. Las unidades nuevas sin capítulos asociados no se verán aquí hasta que exista al menos un capítulo que las use.
        </p>
      </template>
    </CardPrime>

    <DataTablePrime
      :value="unidadesFiltradas"
      :loading="loading"
      class="p-datatable-sm"
      responsiveLayout="scroll"
      :emptyMessage="puedeCargar ? 'Sin unidades en este curso/asignatura' : 'Selecciona curso y asignatura'"
    >
      <ColumnPrime field="Id" header="ID" />
      <ColumnPrime field="Glosa" header="Glosa" />
      <ColumnPrime header="Etiqueta (capítulos)" field="EtiquetaContexto" />
      <ColumnPrime header="Vigente">
        <template #body="{ data }">{{ data.Vigente ? 'Sí' : 'No' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Acciones">
        <template #body="{ data }">
          <ButtonPrime icon="pi pi-pencil" class="p-button-sm p-mr-1" @click="abrirEditar(data)" />
          <ButtonPrime
            v-if="data.Vigente"
            label="Desactivar"
            class="p-button-sm p-button-warning"
            @click="desactivar(data)"
          />
        </template>
      </ColumnPrime>
    </DataTablePrime>

    <DialogPrime v-model:visible="dialogVisible" :modal="true" :header="dialogTitulo" :style="{ width: '420px' }">
      <div class="field mb-3">
        <label class="block mb-1">Glosa</label>
        <InputText v-model="form.Glosa" class="w-full" />
      </div>
      <div class="field mb-3 flex align-items-center">
        <CheckboxPrime v-model="form.Vigente" :binary="true" inputId="uni-vig" />
        <label for="uni-vig" class="ml-2">Vigente</label>
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogVisible = false" />
        <ButtonPrime label="Guardar" @click="guardar" />
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import CardPrime from 'primevue/card';
import DataTablePrime from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import DialogPrime from 'primevue/dialog';
import ButtonPrime from 'primevue/button';
import InputText from 'primevue/inputtext';
import DropdownPrime from 'primevue/dropdown';
import CheckboxPrime from 'primevue/checkbox';
import CursoService from '@/services/CursoService';
import AsignaturaService from '@/services/AsignaturaService';
import CapituloUnidadService from '@/services/CapituloUnidadService';
import SobjectService from '@/services/SobjectService';

export default {
  name: 'MantenedorUnidades',
  components: {
    CardPrime,
    DataTablePrime,
    ColumnPrime,
    DialogPrime,
    ButtonPrime,
    InputText,
    DropdownPrime,
    CheckboxPrime,
  },
  data() {
    return {
      cursos: [],
      asignaturas: [],
      selectedCursoId: null,
      selectedAsignaturaId: null,
      filtroGlosa: '',
      unidades: [],
      loading: false,
      dialogVisible: false,
      dialogTitulo: 'Unidad',
      form: { Id: 0, Glosa: '', Vigente: true },
    };
  },
  computed: {
    puedeCargar() {
      return this.selectedCursoId != null && this.selectedAsignaturaId != null;
    },
    unidadesFiltradas() {
      const q = (this.filtroGlosa || '').trim().toLowerCase();
      if (!q) return this.unidades;
      return this.unidades.filter(
        (u) =>
          (u.Glosa && u.Glosa.toLowerCase().includes(q)) ||
          (u.EtiquetaContexto && u.EtiquetaContexto.toLowerCase().includes(q))
      );
    },
  },
  methods: {
    async cargarCombos() {
      const [c, a] = await Promise.all([CursoService.get(), AsignaturaService.get()]);
      this.cursos = c.data || [];
      this.asignaturas = a.data || [];
    },
    aplicarFiltros() {
      if (!this.puedeCargar) {
        alert('Selecciona curso y asignatura.');
        return;
      }
      this.cargarLista();
    },
    async cargarLista() {
      this.loading = true;
      this.unidades = [];
      try {
        const { data } = await SobjectService.getDataSubjects(this.selectedCursoId, this.selectedAsignaturaId);
        const chapters = data?.Capitulos || [];
        const porUnidad = new Map();
        chapters.forEach((ch) => {
          const uid = ch.UnidadId;
          if (uid == null || uid === undefined) return;
          if (!porUnidad.has(uid)) {
            porUnidad.set(uid, ch.UnidadGrupo || '');
          }
        });
        const ids = [...porUnidad.keys()];
        const detalles = await Promise.all(
          ids.map(async (id) => {
            try {
              const r = await CapituloUnidadService.obtenerUnidadPorId(id);
              const u = r.data || {};
              return {
                Id: u.Id ?? id,
                Glosa: u.Glosa || '',
                Vigente: !!u.Vigente,
                EtiquetaContexto: porUnidad.get(id) || '',
              };
            } catch {
              return {
                Id: id,
                Glosa: porUnidad.get(id) || `(id ${id})`,
                Vigente: true,
                EtiquetaContexto: porUnidad.get(id) || '',
              };
            }
          })
        );
        this.unidades = detalles;
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al cargar estructura curso/asignatura');
      } finally {
        this.loading = false;
      }
    },
    abrirNuevo() {
      if (!this.puedeCargar) return;
      this.dialogTitulo = 'Nueva unidad';
      this.form = { Id: 0, Glosa: '', Vigente: true };
      this.dialogVisible = true;
    },
    abrirEditar(row) {
      this.dialogTitulo = 'Editar unidad';
      this.form = { Id: row.Id, Glosa: row.Glosa, Vigente: !!row.Vigente };
      this.dialogVisible = true;
    },
    async guardar() {
      const glosa = (this.form.Glosa || '').trim();
      if (!glosa) {
        alert('Ingresa la glosa.');
        return;
      }
      const payload = { Id: this.form.Id || 0, Glosa: glosa, Vigente: !!this.form.Vigente };
      try {
        if (payload.Id > 0) {
          await CapituloUnidadService.actualizarUnidad(payload.Id, payload);
        } else {
          await CapituloUnidadService.guardarUnidad(payload);
          alert(
            'Unidad creada. Debes asociarla a capítulos en este curso/asignatura para que aparezca en el listado filtrado.'
          );
        }
        this.dialogVisible = false;
        if (this.puedeCargar) await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al guardar');
      }
    },
    async desactivar(row) {
      if (!confirm(`¿Desactivar la unidad ${row.Id}?`)) return;
      try {
        await CapituloUnidadService.actualizarUnidad(row.Id, {
          Id: row.Id,
          Glosa: row.Glosa,
          Vigente: false,
        });
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error');
      }
    },
  },
  async mounted() {
    await this.cargarCombos();
  },
};
</script>
